import {gql} from '@apollo/client';
import {isPositiveNumber} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {MixtureMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  MixtureGroupParentMixtureQuery,
  MixtureGroupParentMixtureQueryVariables,
} from 'notebook/integrations/registry/__generated/MixtureGroupParentMixtureQuery';
import {IngredientDataFromParent, NumericString} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {
  getMixtureAmountColumnIndex,
  getMixtureAmountFromTable,
  getParentMixtureColumnIndex,
  getParentMixtureIdFromTable,
  INVALID_PARENT_MIXTURE_ID,
  MixtureMetadataStructuredTable,
  ParentMixtureIdFromTableResult,
} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {MixtureGroup} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {
  getUpdatedTableWithIngredientRowsFromParent,
  getUpdatedTableWithoutIngredientRowsFromParent,
  getUpdatedTableWithUnitsFromParent,
} from 'notebook/integrations/registry/MixtureTableUpdaters';
import {
  findNotePartForStructuredTable,
  reduceGroupUpdateNotePartItem,
} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isCellLoading} from 'notebook/tables/excess/Util';
import {coerceToPrimitive, getIdsFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {isMixtureMetadataStructuredTable} from 'notebook/TypePredicates';
import {NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {DEFAULT_MIXTURE_AMOUNT} from 'registry/mixtures/MixtureConstants';
import {MaybePromise, runAfterResolve} from 'util/Promises';

type MixtureGroupParentIngredient = ExtractByTypename<
  MixtureGroupParentMixtureQuery['mixture'],
  'MixtureNode'
>['ingredients'][number];

export const MIXTURE_GROUP_PARENT_MIXTURE_QUERY: DocumentNode = gql`
  query MixtureGroupParentMixtureQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        ...MixtureSpecificAttributesGraphQL
        ingredients {
          ...IngredientGraphQL
          componentBioentity {
            id
            displayLabel
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function shouldChildIngredientBeInherited(parentIngredient: MixtureGroupParentIngredient): boolean {
  // Keep in sync with benchling/registry/mixtures/models/ingredient.py:_maybe_inherit_from_parent
  return (
    Boolean(parentIngredient.amount) &&
    Boolean(parentIngredient.componentBioentity) &&
    Boolean(parentIngredient.units)
  );
}

export function getIngredientsToCopyFromParent(
  parentIngredients: Array<MixtureGroupParentIngredient>
): Array<IngredientDataFromParent> {
  return parentIngredients.map((parentIngredient) => {
    const {componentBioentity, units, catalogIdentifier, id, amount, notes} = parentIngredient;
    return {
      componentBioentitySummary: {id: componentBioentity.id, displayLabel: componentBioentity.displayLabel},
      units,
      catalogIdentifier,
      notes,
      parentIngredientId: id,
      parentIngredientAmount: shouldChildIngredientBeInherited(parentIngredient)
        ? {stringValue: amount!, numericValue: Number(amount)}
        : null,
    };
  });
}

/**
 * This reducer is responsible for updating 2 pieces of information in the ingredient table note part:
 *   - Add rows to the table for ingredients being copied/inherited from the parent
 *   - Set the table's metdata.parentMixtureAmount value based on the new parent's amount
 */
function reduceGroupUpdateIngredientTableForNewParent(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  ingredientsFromParent: Array<IngredientDataFromParent>,
  amountFromParent: string,
  parentMixtureId: string
): MixtureGroup {
  const currentIngredientTable = ingredientTableNotePart.item;
  const tableWithUpdatedRows =
    ingredientsFromParent.length > 0
      ? getUpdatedTableWithIngredientRowsFromParent(currentIngredientTable, ingredientsFromParent)
      : currentIngredientTable;
  const updatedIngredientTable = {
    ...tableWithUpdatedRows,
    metadata: {
      ...tableWithUpdatedRows.metadata,
      parentMixtureId,
      ...(ingredientsFromParent.length > 0
        ? {
            parentMixtureAmount: {
              stringValue: amountFromParent,
              numericValue: Number(amountFromParent),
            },
          }
        : {}),
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

function reduceGroupUpdateMetadataTableForNewParent(
  noteLinkedGroup: MixtureGroup,
  metadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  unitsFromParent: MixtureMeasurementUnit
): MixtureGroup {
  const currentMetadataTable = metadataTableNotePart.item;
  const updatedMetadataTable = getUpdatedTableWithUnitsFromParent(currentMetadataTable, unitsFromParent);
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, metadataTableNotePart, updatedMetadataTable);
}

function reduceGroupUpdateIngredientTableForRemovedParent(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>
): MixtureGroup {
  const currentIngredientTable = ingredientTableNotePart.item;
  const tableWithUpdatedRows = getUpdatedTableWithoutIngredientRowsFromParent(currentIngredientTable);
  const updatedIngredientTable = {
    ...tableWithUpdatedRows,
    metadata: {
      ...tableWithUpdatedRows.metadata,
      parentMixtureId: null,
      parentMixtureAmount: null,
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

async function asyncReduceGroupHandleNewParentMixture(
  noteLinkedGroup: MixtureGroup,
  metadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  newParentMixtureId: string
): Promise<MixtureGroup> {
  const data = await doQuerySafe<MixtureGroupParentMixtureQuery, MixtureGroupParentMixtureQueryVariables>(
    {query: MIXTURE_GROUP_PARENT_MIXTURE_QUERY},
    {variables: {mixtureId: newParentMixtureId}}
  );
  const parentMixture = data.mixture;
  assert(
    !isInaccessible(parentMixture),
    'Expected parent mixture selected by user in notebook to be readable'
  );
  const updatedForParentIngredients = reduceGroupUpdateIngredientTableForNewParent(
    noteLinkedGroup,
    ingredientTableNotePart,
    getIngredientsToCopyFromParent(parentMixture.ingredients),
    parentMixture.amount,
    newParentMixtureId
  );
  return reduceGroupUpdateMetadataTableForNewParent(
    updatedForParentIngredients,
    metadataTableNotePart,
    parentMixture.units
  );
}

function findIngredientTableNotePartOrFail(
  noteLinkedGroup: MixtureGroup
): NotePartWithItem<MixtureIngredientsStructuredTable> {
  const ingredientTableNotePart = findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
    noteLinkedGroup,
    StructuredTableKind.MIXTURE_INGREDIENTS
  );
  assert(
    ingredientTableNotePart != null,
    'Expected mixture group to contain a NotePart corresponding to the mixture ingredients structured table'
  );
  return ingredientTableNotePart;
}

/**
 * Used when ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is off. Will be deleted once the feature
 * migration completes; until then the form-based renderer can't surface invalid input or chain
 * a clear+select in a single operation, so the simpler "clear or set" structure is sufficient.
 */
function reduceGroupHandleParentMixtureChangeLegacy(
  noteLinkedGroup: MixtureGroup,
  updatedMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  currentParentMixtureId: ParentMixtureIdFromTableResult,
  updatedParentMixtureId: ParentMixtureIdFromTableResult
): MaybePromise<MixtureGroup> {
  if (currentParentMixtureId === updatedParentMixtureId) {
    // If the parent mixture ID didn't change, then no update to the group is needed
    return noteLinkedGroup;
  }
  const ingredientTableNotePart = findIngredientTableNotePartOrFail(noteLinkedGroup);
  if (updatedParentMixtureId == null) {
    return reduceGroupUpdateIngredientTableForRemovedParent(noteLinkedGroup, ingredientTableNotePart);
  }
  // The legacy reader only ever returns `string | null`, so we know updatedParentMixtureId is a
  // string here (the sentinel branch only fires under the new tolerant reader).
  assert(
    typeof updatedParentMixtureId === 'string',
    'Expected updatedParentMixtureId to be a string under the legacy parent-mixture reader'
  );
  return asyncReduceGroupHandleNewParentMixture(
    noteLinkedGroup,
    updatedMetadataTableNotePart,
    ingredientTableNotePart,
    updatedParentMixtureId
  );
}

/**
 * When ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is on, this reducer:
 *   - Bails when {@link getParentMixtureIdFromTable} returns {@link INVALID_PARENT_MIXTURE_ID}
 *     (the tolerant reader's "invalid input" sentinel), instead of treating that case as "parent
 *     was cleared" and wiping the ingredient table.
 *   - When the parent changes to a valid new mixture, proactively clears any ingredient rows
 *     inherited from the previous parent before fetching and inserting the new parent's rows.
 *     The Single Input UI can clear an old parent value and select a new parent as a single
 *     operation, so we cannot rely on a prior "removed parent" reduction having run.
 */
export function reduceGroupHandleParentMixtureChange(
  noteLinkedGroup: MixtureGroup,
  currentMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  updatedMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>
): MaybePromise<MixtureGroup> {
  assert(
    [currentMetadataTableNotePart, updatedMetadataTableNotePart].every(
      (notePart) => notePart.item && isMixtureMetadataStructuredTable(notePart.item)
    ),
    'Note parts provided to reduceGroupHandleParentMixtureChange must correspond to a mixture metadata structured table'
  );
  const currentMetadataTable = currentMetadataTableNotePart.item;
  const updatedMetadataTable = updatedMetadataTableNotePart.item;
  const currentParentMixtureId = getParentMixtureIdFromTable(currentMetadataTable);
  const updatedParentMixtureId = getParentMixtureIdFromTable(updatedMetadataTable);
  if (!BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION) {
    return reduceGroupHandleParentMixtureChangeLegacy(
      noteLinkedGroup,
      updatedMetadataTableNotePart,
      currentParentMixtureId,
      updatedParentMixtureId
    );
  }
  if (updatedParentMixtureId === INVALID_PARENT_MIXTURE_ID) {
    return noteLinkedGroup;
  }
  if (currentParentMixtureId === updatedParentMixtureId) {
    return noteLinkedGroup;
  }
  const ingredientTableNotePart = findIngredientTableNotePartOrFail(noteLinkedGroup);
  if (updatedParentMixtureId == null) {
    return reduceGroupUpdateIngredientTableForRemovedParent(noteLinkedGroup, ingredientTableNotePart);
  }
  const groupAfterClearingOldParent = reduceGroupUpdateIngredientTableForRemovedParent(
    noteLinkedGroup,
    ingredientTableNotePart
  );
  const ingredientTableNotePartAfterClearing = findIngredientTableNotePartOrFail(groupAfterClearingOldParent);
  return asyncReduceGroupHandleNewParentMixture(
    groupAfterClearingOldParent,
    updatedMetadataTableNotePart,
    ingredientTableNotePartAfterClearing,
    updatedParentMixtureId
  );
}

function reduceGroupUpdateIngredientTableForNewMixturePrepAmount(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  updatedMixturePrepAmount: NumericString
): MixtureGroup {
  const currentIngredientTable = ingredientTableNotePart.item;
  const updatedIngredientTable = {
    ...currentIngredientTable,
    metadata: {
      ...currentIngredientTable.metadata,
      mixtureAmount: updatedMixturePrepAmount,
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

export function reduceGroupHandleUpdatedMixturePrepAmount(
  noteLinkedGroup: MixtureGroup,
  currentMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  updatedMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>
): MixtureGroup {
  assert(
    [currentMetadataTableNotePart, updatedMetadataTableNotePart].every(
      (notePart) => notePart.item && isMixtureMetadataStructuredTable(notePart.item)
    ),
    'Note parts provided to reduceGroupHandleUpdatedMixturePrepAmount must correspond to a mixture metadata structured table'
  );
  const currentMetadataTable = currentMetadataTableNotePart.item;
  const updatedMetadataTable = updatedMetadataTableNotePart.item;
  const currentAmount = getMixtureAmountFromTable(currentMetadataTable).trim();
  const updatedAmount = getMixtureAmountFromTable(updatedMetadataTable).trim();

  if (isPositiveNumber(updatedAmount) && updatedAmount !== currentAmount) {
    // Only update the group when the amount has changed and the new value represents a valid number
    const ingredientTableNotePart = findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
      noteLinkedGroup,
      StructuredTableKind.MIXTURE_INGREDIENTS
    );
    assert(
      ingredientTableNotePart != null,
      'Expected mixture group to contain a NotePart corresponding to the mixture ingredients structured table'
    );
    return reduceGroupUpdateIngredientTableForNewMixturePrepAmount(noteLinkedGroup, ingredientTableNotePart, {
      stringValue: updatedAmount,
      numericValue: Number(updatedAmount),
    });
  }
  return noteLinkedGroup;
}

/**
 * Returns the single mixture link ID encoded in an evaluated parent-mixture cell, or null if the
 * cell's evaluated value cannot be canonicalized to exactly one link. It is the responsibility of
 * the metadata column to validate that the typed value is a valid mixture parent.
 */
export function getEvaluatedParentMixtureId(evaluatedCell: EvaluatedCell): string | null {
  const ids = getIdsFromTypedValue(evaluatedCell.value);
  if (ids == null || ids.length !== 1) {
    return null;
  }
  return ids[0];
}

/**
 * Returns the mixture prep amount encoded in an evaluated amount cell as a string, or null if the
 * evaluated value cannot be coerced to a valid positive numeric string. Mirrors the acceptance rules
 * in {@link reduceGroupHandleUpdatedMixturePrepAmount}.
 */
export function getEvaluatedMixtureAmount(evaluatedCell: EvaluatedCell): string | null {
  const primitive = coerceToPrimitive(evaluatedCell.value);
  let candidate: string | null = null;
  if (typeof primitive === 'string') {
    candidate = primitive;
  } else if (typeof primitive === 'number' && Number.isFinite(primitive)) {
    candidate = String(primitive);
  }
  if (candidate == null || !isPositiveNumber(candidate)) {
    return null;
  }
  return candidate;
}

/**
 * Returns true if the ingredients table's metadata values for parentMixtureId and mixtureAmount are
 * out of sync with the metadata table.
 */
export function doesMixtureGroupRequireResync(
  group: MixtureGroup,
  evaluatedTableMap: OutputTableMap
): boolean {
  if (!BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION) {
    return false;
  }
  const metadataNotePart = findNotePartForStructuredTable<MixtureMetadataStructuredTable>(
    group,
    StructuredTableKind.MIXTURE_METADATA
  );
  const ingredientsNotePart = findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
    group,
    StructuredTableKind.MIXTURE_INGREDIENTS
  );
  if (metadataNotePart == null || ingredientsNotePart == null) {
    return false;
  }
  const metadataTable = metadataNotePart.item;
  const ingredientsMetadata = ingredientsNotePart.item.metadata;
  const evaluatedMetadataTable = evaluatedTableMap[metadataTable.name];
  if (evaluatedMetadataTable == null) {
    return false;
  }
  const evaluatedRow = evaluatedMetadataTable[0];
  if (evaluatedRow == null) {
    return false;
  }

  const amountColumnIndex = getMixtureAmountColumnIndex(metadataTable);
  const amountCell = evaluatedRow[amountColumnIndex];
  if (amountCell == null || isCellLoading(amountCell)) {
    return false;
  }
  const evaluatedAmount = getEvaluatedMixtureAmount(amountCell) ?? DEFAULT_MIXTURE_AMOUNT.numericValue;
  // When the reducer has never written `mixtureAmount` (e.g. a freshly-created group whose user
  // never edited the amount), the persisted value defaults to DEFAULT_MIXTURE_AMOUNT downstream
  // (see computeTargetAmount's `tableMetadata.mixtureAmount || DEFAULT_MIXTURE_AMOUNT` fallback),
  // so we treat undefined here as the default and avoid flagging stale state when the evaluated
  // cell is still at the default.
  const persistedAmountNumericValue =
    ingredientsMetadata.mixtureAmount?.numericValue ?? DEFAULT_MIXTURE_AMOUNT.numericValue;
  if (Number(evaluatedAmount) !== persistedAmountNumericValue) {
    return true;
  }

  const parentColumnIndex = getParentMixtureColumnIndex(metadataTable.columns, metadataTable.metadata);
  if (parentColumnIndex === -1) {
    return false;
  }
  const parentCell = evaluatedRow[parentColumnIndex];
  if (parentCell == null || isCellLoading(parentCell)) {
    return false;
  }
  const evaluatedParentMixtureId = getEvaluatedParentMixtureId(parentCell);
  const persistedParentMixtureId = ingredientsMetadata.parentMixtureId ?? null;
  return evaluatedParentMixtureId !== persistedParentMixtureId;
}

/**
 * Resyncs the ingredients table's rows and metadata against the latest evaluated values for the
 * mixture group's metadata table.
 *
 * The contract is intentionally narrow: the only cases where this function makes no change are
 * (1) the values already agree, or (2) one of the relevant evaluated cells is in a loading state.
 * Any other input — including invalid amounts, missing parent-link columns, or evaluated parent
 * cells that don't canonicalize to a single ID — is treated as a definitive signal to drive
 * persisted ingredients metadata to mirror the metadata-side view. Invalid evaluated amounts
 * collapse to {@link DEFAULT_MIXTURE_AMOUNT}, matching {@link doesMixtureGroupRequireResync}.
 */
export function resyncMixtureGroupComponentsAsync(
  noteLinkedGroup: MixtureGroup,
  evaluatedTableMap: OutputTableMap
): MaybePromise<MixtureGroup> {
  const metadataNotePart = findNotePartForStructuredTable<MixtureMetadataStructuredTable>(
    noteLinkedGroup,
    StructuredTableKind.MIXTURE_METADATA
  );
  assert(metadataNotePart != null, 'Expected mixture group to contain a NotePart for the metadata table');
  const ingredientsNotePart = findIngredientTableNotePartOrFail(noteLinkedGroup);
  const metadataTable = metadataNotePart.item;
  const evaluatedMetadataTable = evaluatedTableMap[metadataTable.name];
  assert(
    evaluatedMetadataTable != null && evaluatedMetadataTable.length === 1,
    'Expected evaluated metadata table to exist when resyncing a mixture group'
  );
  const evaluatedRow = evaluatedMetadataTable[0];

  // Resolve the evaluated parent-link cell to a candidate ID. Bail out when the cell is loading
  // (we can't make a decision yet). When the column is missing or the cell does not canonicalize
  // to a single link, treat the candidate as null — running this reducer drives the
  // ingredients-side state to mirror the metadata-side state whenever the cells aren't loading
  // and the values disagree, matching the behavior of {@link doesMixtureGroupRequireResync}.
  let evaluatedParentMixtureId: string | null = null;
  let evaluatedAmountString: string = DEFAULT_MIXTURE_AMOUNT.stringValue;
  const parentColumnIndex = getParentMixtureColumnIndex(metadataTable.columns, metadataTable.metadata);
  const parentCell = parentColumnIndex !== -1 ? evaluatedRow[parentColumnIndex] : null;
  if (parentCell != null) {
    if (isCellLoading(parentCell)) {
      return noteLinkedGroup;
    }
    evaluatedParentMixtureId = getEvaluatedParentMixtureId(parentCell);
  }

  // Resolve the evaluated amount cell to a candidate string. Bail out when the cell is loading
  // (we can't make a decision yet). When the evaluated value is invalid (cannot be coerced to a
  // positive numeric string), fall back to DEFAULT_MIXTURE_AMOUNT — this matches how
  // {@link doesMixtureGroupRequireResync} treats invalid evaluated amounts as the default and
  // honors the invariant that running this reducer drives the ingredients-side state to mirror
  // the metadata-side state whenever the cells aren't loading and the values disagree.
  const amountColumnIndex = getMixtureAmountColumnIndex(metadataTable);
  const amountCell = evaluatedRow[amountColumnIndex];
  if (amountCell != null) {
    if (isCellLoading(amountCell)) {
      return noteLinkedGroup;
    }
    evaluatedAmountString = (
      getEvaluatedMixtureAmount(amountCell) ?? DEFAULT_MIXTURE_AMOUNT.stringValue
    ).trim();
  }

  // Apply parent-id resync first, since the new-parent loader rewrites ingredient rows and we
  // want to overwrite mixtureAmount on top of that fresh ingredients table.
  const persistedParentMixtureId = ingredientsNotePart.item.metadata.parentMixtureId ?? null;
  const persistedAmountNumericValue =
    ingredientsNotePart.item.metadata.mixtureAmount?.numericValue ?? DEFAULT_MIXTURE_AMOUNT.numericValue;

  const groupAfterParent: MaybePromise<MixtureGroup> = (() => {
    if (evaluatedParentMixtureId === persistedParentMixtureId) {
      return noteLinkedGroup;
    }

    if (evaluatedParentMixtureId == null) {
      return reduceGroupUpdateIngredientTableForRemovedParent(noteLinkedGroup, ingredientsNotePart);
    }
    const groupAfterClearingOldParent = reduceGroupUpdateIngredientTableForRemovedParent(
      noteLinkedGroup,
      ingredientsNotePart
    );
    const ingredientsAfterClearing = findIngredientTableNotePartOrFail(groupAfterClearingOldParent);

    return asyncReduceGroupHandleNewParentMixture(
      groupAfterClearingOldParent,
      metadataNotePart,
      ingredientsAfterClearing,
      evaluatedParentMixtureId
    );
  })();

  return runAfterResolve(groupAfterParent, (groupAfterParentResolved) => {
    if (Number(evaluatedAmountString) === persistedAmountNumericValue) {
      return groupAfterParentResolved;
    }
    const ingredientsNotePartAfterParent = findIngredientTableNotePartOrFail(groupAfterParentResolved);
    return reduceGroupUpdateIngredientTableForNewMixturePrepAmount(
      groupAfterParentResolved,
      ingredientsNotePartAfterParent,
      {
        stringValue: evaluatedAmountString,
        numericValue: Number(evaluatedAmountString),
      }
    );
  });
}
